import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, CreditCard, Plus, Trash2, TrendingDown, Calculator } from 'lucide-react';
import { toast } from 'sonner';

export default function DebtsPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newDebt, setNewDebt] = useState({
    name: '',
    totalAmount: '',
    remainingAmount: '',
    interestRate: '',
    monthlyPayment: ''
  });

  useEffect(() => {
    if (hasAccess) {
      const saved = localStorage.getItem('debt-tracking');
      if (saved) {
        setDebts(JSON.parse(saved));
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const saveDebts = (updated) => {
    setDebts(updated);
    localStorage.setItem('debt-tracking', JSON.stringify(updated));
  };

  const addDebt = () => {
    if (!newDebt.name.trim() || !newDebt.remainingAmount) {
      toast.error('Ange namn och belopp');
      return;
    }
    const debt = {
      id: Date.now(),
      name: newDebt.name,
      totalAmount: Number(newDebt.totalAmount) || Number(newDebt.remainingAmount),
      remainingAmount: Number(newDebt.remainingAmount),
      interestRate: Number(newDebt.interestRate) || 0,
      monthlyPayment: Number(newDebt.monthlyPayment) || 0,
      payments: [],
      createdAt: new Date().toISOString()
    };
    saveDebts([...debts, debt]);
    setNewDebt({ name: '', totalAmount: '', remainingAmount: '', interestRate: '', monthlyPayment: '' });
    setShowForm(false);
    toast.success('Skuld tillagd!');
  };

  const makePayment = (debtId, amount) => {
    const updated = debts.map(d => {
      if (d.id === debtId) {
        const newRemaining = Math.max(0, d.remainingAmount - amount);
        return {
          ...d,
          remainingAmount: newRemaining,
          payments: [...d.payments, { amount, date: new Date().toISOString() }]
        };
      }
      return d;
    });
    saveDebts(updated);
    toast.success('Betalning registrerad!');
  };

  const deleteDebt = (debtId) => {
    saveDebts(debts.filter(d => d.id !== debtId));
    toast.success('Skuld borttagen');
  };

  const totalDebt = debts.reduce((sum, d) => sum + d.remainingAmount, 0);
  const totalOriginal = debts.reduce((sum, d) => sum + d.totalAmount, 0);
  const totalPaid = totalOriginal - totalDebt;

  // Calculate months to debt-free
  const calculateMonthsLeft = (debt) => {
    if (debt.monthlyPayment <= 0 || debt.remainingAmount <= 0) return null;
    const monthlyInterest = debt.interestRate / 100 / 12;
    if (monthlyInterest === 0) {
      return Math.ceil(debt.remainingAmount / debt.monthlyPayment);
    }
    // With interest
    const months = Math.log(debt.monthlyPayment / (debt.monthlyPayment - debt.remainingAmount * monthlyInterest)) / Math.log(1 + monthlyInterest);
    return Math.ceil(months);
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="debts-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Skulder</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till skuldspårning.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="debts-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Skulder</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-2">
          <CreditCard className="w-12 h-12 mx-auto text-teal-500" />
          <h2 className="font-heading text-2xl font-semibold">Skulder</h2>
          <p className="text-muted-foreground">Överblick och avbetalningsplan</p>
        </div>

        {/* Summary */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-4 border border-red-100">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-red-700">Kvar att betala</p>
              <p className="text-2xl font-bold text-red-800">{totalDebt.toLocaleString('sv-SE')} kr</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-green-700">Redan betalt</p>
              <p className="text-2xl font-bold text-green-700">{totalPaid.toLocaleString('sv-SE')} kr</p>
            </div>
          </div>
          {totalOriginal > 0 && (
            <div className="mt-3">
              <div className="h-2 bg-red-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all rounded-full"
                  style={{ width: `${Math.min(100, (totalPaid / totalOriginal) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {Math.round((totalPaid / totalOriginal) * 100)}% avbetalat
              </p>
            </div>
          )}
        </div>

        {/* Add button */}
        <Button onClick={() => setShowForm(true)} className="w-full rounded-full" variant="outline">
          <Plus className="w-5 h-5 mr-2" />
          Lägg till skuld
        </Button>

        {/* Add form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            <h3 className="font-semibold">Lägg till skuld</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm text-muted-foreground">Namn *</label>
                <Input
                  value={newDebt.name}
                  onChange={(e) => setNewDebt({ ...newDebt, name: e.target.value })}
                  placeholder="T.ex. Billån, CSN, Kreditkort"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Kvar att betala (kr) *</label>
                <Input
                  type="number"
                  value={newDebt.remainingAmount}
                  onChange={(e) => setNewDebt({ ...newDebt, remainingAmount: e.target.value })}
                  placeholder="T.ex. 150000"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Ursprungligt belopp (kr)</label>
                <Input
                  type="number"
                  value={newDebt.totalAmount}
                  onChange={(e) => setNewDebt({ ...newDebt, totalAmount: e.target.value })}
                  placeholder="T.ex. 200000"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Ränta (%)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={newDebt.interestRate}
                  onChange={(e) => setNewDebt({ ...newDebt, interestRate: e.target.value })}
                  placeholder="T.ex. 5.5"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-muted-foreground">Månadsbetalning (kr)</label>
                <Input
                  type="number"
                  value={newDebt.monthlyPayment}
                  onChange={(e) => setNewDebt({ ...newDebt, monthlyPayment: e.target.value })}
                  placeholder="T.ex. 3000"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-full">
                Avbryt
              </Button>
              <Button onClick={addDebt} className="rounded-full">
                Lägg till
              </Button>
            </div>
          </div>
        )}

        {/* Debts list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-teal-200 border-t-teal-500 rounded-full animate-spin" />
          </div>
        ) : debts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Inga skulder registrerade. 🎉</p>
          </div>
        ) : (
          <div className="space-y-4">
            {debts.map(debt => {
              const progress = ((debt.totalAmount - debt.remainingAmount) / debt.totalAmount) * 100;
              const monthsLeft = calculateMonthsLeft(debt);
              const isPaidOff = debt.remainingAmount === 0;
              
              return (
                <div key={debt.id} className={`bg-white rounded-2xl p-5 border ${isPaidOff ? 'border-green-200 bg-green-50' : 'border-border/30'}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{debt.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {debt.remainingAmount.toLocaleString('sv-SE')} kr kvar
                        {debt.interestRate > 0 && ` • ${debt.interestRate}% ränta`}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteDebt(debt.id)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
                    <div 
                      className={`h-full transition-all rounded-full ${isPaidOff ? 'bg-green-500' : 'bg-teal-500'}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between items-center text-sm mb-4">
                    <span className={isPaidOff ? 'text-green-600 font-semibold' : 'text-muted-foreground'}>
                      {isPaidOff ? '🎉 Avbetalat!' : `${Math.round(progress)}% avbetalat`}
                    </span>
                    {monthsLeft && !isPaidOff && (
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Calculator className="w-3 h-3" />
                        ~{monthsLeft} månader kvar
                      </span>
                    )}
                  </div>

                  {/* Quick payment buttons */}
                  {!isPaidOff && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => makePayment(debt.id, 500)}
                        className="rounded-full flex-1"
                      >
                        Betala 500 kr
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => makePayment(debt.id, 1000)}
                        className="rounded-full flex-1"
                      >
                        Betala 1000 kr
                      </Button>
                      {debt.monthlyPayment > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => makePayment(debt.id, debt.monthlyPayment)}
                          className="rounded-full flex-1"
                        >
                          Betala {debt.monthlyPayment} kr
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Tips */}
        <div className="bg-teal-50 rounded-2xl p-4 border border-teal-100">
          <p className="text-teal-800 text-sm">
            💡 <strong>Snöbollsmetoden:</strong> Börja med den minsta skulden för snabba vinster, eller <strong>lavinmetoden:</strong> börja med högst ränta för lägst totalkostnad.
          </p>
        </div>
      </main>
    </div>
  );
}
